/**
 * SiteAdminTemplateVisibility — iter63z (template-visibility)
 *
 * Site-Admin control over the per-template visibility state shown to
 * league admins in TournamentSetup. Four states per template:
 *
 *   live        — Card is selectable as today (default for all templates)
 *   coming_soon — Card rendered grayed-out with "Coming soon" badge
 *   closed      — Card rendered grayed-out with "Closed" badge (a season
 *                 that ran has ended — not selectable for NEW tournaments,
 *                 existing tournaments on this template are unaffected)
 *   hidden      — Card not rendered at all
 *
 * The override map is persisted at the global level (one document in
 * `system_settings`). Missing entries fall through to the hardcoded
 * defaults in TournamentSetup.jsx's SPORTS / TEMPLATE_PRESETS arrays.
 *
 * Notes:
 *  - This sits alongside (NOT replacing) the existing
 *    `SiteAdminTemplateAvailability` component which controls a simpler
 *    boolean disable list. The two mechanisms compose: a template is
 *    disabled in TournamentSetup if EITHER its visibility override is
 *    "hidden"/"coming_soon"/"closed" OR it's in the disabled list. The
 *    newer 4-state control is recommended going forward.
 *  - Keys must stay in sync with SPORTS + TEMPLATE_PRESETS in
 *    TournamentSetup.jsx. We use a shared key namespace (e.g. "olympics"
 *    controls both the sport tile AND the template tile).
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Eye, EyeOff, Hourglass, Lock } from "lucide-react";

// Canonical list, grouped by sport to mirror the consolidated picker
// (iter86.45). Each group leads with its sport tile key; template keys
// nest beneath. Shared key namespace with SPORTS + TEMPLATE_PRESETS.
const GROUPS = [
    {
        label: "Soccer", icon: "⚽",
        rows: [
            { key: "soccer",           label: "Soccer (sport tile)", sport: true },
            { key: "world_cup_api",    label: "Tournament — Groups + Knockout (World Cup)" },
            { key: "world_cup_ko_api", label: "Tournament — Knockout only (World Cup)" },
            { key: "champions_league", label: "Tournament — European Champions League" },
            { key: "womens_world_cup", label: "Tournament — Women's World Cup" },
            { key: "epl_boost_api",     label: "Weekly Spread Boost — Premier League" },
            { key: "la_liga_boost_api", label: "Weekly Spread Boost — La Liga" },
            // iter87.199.181 — Club Season used to be ONE blanket key
            // ("club_season") with "which leagues appear" governed by
            // Event Bindings (data-cache presence), not by an actual
            // visibility decision. Split into one key per builtin
            // league (matches CLUB_LEAGUES in tournament-setup/constants.js)
            // so each can be flipped independently, same as the Weekly
            // Spread Boost rows above. Event Bindings now purely control
            // cached fixture/odds data — they no longer decide exposure.
            // iter87.234.5 (operator ask) — Premier League / La Liga no
            // longer have a "full season" variant (iter87.233 — 1st/2nd
            // Half tiles only), so each HALF gets its own visibility
            // key too — e.g. exposing only the 2nd Half without the 1st.
            { key: "premier_league_api__1h", label: "Club Season — Premier League — 1st Half" },
            { key: "premier_league_api__2h", label: "Club Season — Premier League — 2nd Half" },
            { key: "la_liga_api__1h",        label: "Club Season — La Liga — 1st Half" },
            { key: "la_liga_api__2h",        label: "Club Season — La Liga — 2nd Half" },
            { key: "champions_league_api", label: "Club Season — Champions League (UCL)" },
            { key: "nwsl_playoffs_api", label: "NWSL Playoffs — Champions Pick + Dark Horse Pick + Playoff Predictor" },
            { key: "mls_playoffs_api", label: "MLS Playoffs — Champions + Dark Horse + Play-In Bonus + Series/Playoff Predictor" },
            { key: "ucl_playoffs_api", label: "UCL Playoffs — Champion + Dark Horse + Two-Leg Predictor + Champions Predictor" },
        ],
    },
    {
        label: "Golf", icon: "⛳",
        rows: [
            { key: "golf",                       label: "Golf (sport tile)", sport: true },
            { key: "golf_pickem_lowest_avg_api", label: "Lowest Average Score" },
            { key: "golf_position_draft_api",    label: "Position Draft" },
        ],
    },
    {
        label: "Football", icon: "🏈",
        rows: [
            { key: "football",       label: "Football (sport tile)", sport: true },
            { key: "nfl_season_api", label: "NFL Season — Pot Pick'em" },
            { key: "nfl_boost_api",  label: "NFL — Weekly Spread Boost" },
            { key: "nfl_playoffs_api", label: "NFL Playoffs — Super Bowl Champion + Dark Horse + Playoff Predictor" },
            { key: "cfb_playoffs_api", label: "CFB Playoffs — Playoff Picks + Playoff Predictor" },
        ],
    },
    {
        label: "Basketball", icon: "🏀",
        rows: [
            { key: "basketball",    label: "Basketball (sport tile)", sport: true },
            { key: "nba_playoffs_api",  label: "NBA Playoffs" },
            { key: "march_madness", label: "March Madness" },
        ],
    },
    {
        label: "Baseball", icon: "⚾",
        rows: [
            { key: "baseball",          label: "Baseball (sport tile)", sport: true },
            { key: "mlb_playoffs_api",  label: "MLB Playoffs — Team Pick Pot + Champions Pick + Series Predictor" },
        ],
    },
    {
        label: "Olympics", icon: "🏅",
        rows: [
            { key: "olympics", label: "Olympics (sport tile + template — shared key)", sport: true },
        ],
    },
    {
        label: "Squares", icon: "🔲",
        rows: [
            { key: "squares", label: "Squares (sport tile) — Pro+ only", sport: true },
            { key: "nfl_squares_manual_api", label: "NFL — Pick Your Boxes (Pro+ only)" },
            { key: "nfl_squares_auto_api", label: "NFL — Luck of the Draw (Pro+ only)" },
            { key: "cfb_squares_manual_api", label: "CFB — Pick Your Boxes (Pro+ only)" },
            { key: "cfb_squares_auto_api", label: "CFB — Luck of the Draw (Pro+ only)" },
        ],
    },
    {
        label: "Custom", icon: "🛠️",
        rows: [
            { key: "custom", label: "Custom (sport tile + template — shared key; wizard visibility + integrations only, not tournament creation)", sport: true },
        ],
    },
];

const STATES = [
    { value: "live",        label: "Live",         icon: Eye,       color: "text-neon",     desc: "Selectable" },
    { value: "coming_soon", label: "Coming soon",  icon: Hourglass, color: "text-zinc-400", desc: "Grayed out" },
    { value: "closed",      label: "Closed",       icon: Lock,      color: "text-amber-400", desc: "Season ended — grayed out" },
    { value: "hidden",      label: "Hidden",       icon: EyeOff,    color: "text-rose-400", desc: "Not shown" },
];

export default function SiteAdminTemplateVisibility() {
    const [overrides, setOverrides] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.getTemplateVisibility();
            setOverrides(r?.overrides && typeof r.overrides === "object" ? { ...r.overrides } : {});
        } catch (e) {
            toast.error("Couldn't load template visibility settings");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const stateFor = (key) => overrides[key] || "live";

    const setState = async (key, newState) => {
        const current = stateFor(key);
        if (current === newState) return;
        const next = { ...overrides };
        if (newState === "live") delete next[key];
        else next[key] = newState;
        setSaving(true);
        try {
            const r = await api.updateTemplateVisibility(next);
            setOverrides(r?.overrides && typeof r.overrides === "object" ? { ...r.overrides } : {});
            toast.success(`${key} → ${newState}`, { duration: 2000 });
        } catch (e) {
            toast.error("Couldn't update visibility");
        }
        setSaving(false);
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="site-template-visibility-card">
            <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-cyber" />
                    Template Visibility
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                    Per game-type control over what rivalry admins see in Tournament Setup. Club Season leagues are each their own row — Premier League / La Liga are split into 1st Half / 2nd Half rows (no more "full season" variant for those two), Champions League stays one row. Event Bindings below only control which leagues have live fixture/odds data cached, not whether they're exposed here. <span className="text-zinc-400">Site Admin's tournament-create UI is unaffected — you'll always see every template here.</span>
                </p>
            </div>

            {loading && <p className="text-xs text-zinc-500">Loading…</p>}

            {!loading && (
                <div className="space-y-4">
                    {GROUPS.map((group) => {
                        const counts = group.rows.reduce((acc, r) => {
                            acc[stateFor(r.key)] = (acc[stateFor(r.key)] || 0) + 1;
                            return acc;
                        }, {});
                        return (
                            <div key={group.label} data-testid={`template-visibility-group-${group.label.toLowerCase()}`}>
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-base">{group.icon}</span>
                                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-300">{group.label}</span>
                                    <span className="text-[10px] text-zinc-600 font-mono">
                                        {[counts.live && `${counts.live} live`, counts.coming_soon && `${counts.coming_soon} soon`, counts.closed && `${counts.closed} closed`, counts.hidden && `${counts.hidden} hidden`].filter(Boolean).join(" · ")}
                                    </span>
                                </div>
                                <div className="space-y-1">
                                    {group.rows.map((row) => {
                                        const current = stateFor(row.key);
                                        return (
                                            <div
                                                key={row.key}
                                                data-testid={`template-visibility-row-${row.key}`}
                                                className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded border border-[#222] bg-ink/40 ${row.sport ? "" : "ml-4 sm:ml-6"}`}
                                            >
                                                <span className={`truncate text-sm ${row.sport ? "font-bold text-white" : "text-zinc-300"}`}>
                                                    {row.label}
                                                </span>
                                                <div className="flex gap-1 shrink-0">
                                                    {STATES.map((st) => {
                                                        const Icon = st.icon;
                                                        const active = current === st.value;
                                                        return (
                                                            <button
                                                                key={st.value}
                                                                type="button"
                                                                onClick={() => setState(row.key, st.value)}
                                                                disabled={saving}
                                                                data-testid={`template-visibility-${row.key}-${st.value}`}
                                                                title={`${st.label} — ${st.desc}`}
                                                                className={`px-2 py-1 rounded border-2 transition-colors flex items-center gap-1 ${
                                                                    active
                                                                        ? `border-cyber bg-cyber/10 ${st.color}`
                                                                        : "border-[#333] bg-ink text-zinc-500 hover:border-[#555]"
                                                                } ${saving ? "opacity-50 cursor-wait" : ""}`}
                                                            >
                                                                <Icon className="w-3.5 h-3.5" />
                                                                <span className="text-[9px] font-bold uppercase tracking-wider hidden md:inline">
                                                                    {st.label}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <p className="text-[10px] text-zinc-600 italic">
                Changes apply immediately to all rivalry admins. "Live" is the default and isn't persisted — a template with no override behaves as its hardcoded default in TournamentSetup.
            </p>
        </div>
    );
}
